import { ManualMimshakData } from "../interfaces/DTO/manualMimshakData";
import { BaseService } from "./BaseService";
import { MimshakDataServiceBase } from "./mimshakDataServiceBase";
import apiClient from "../core/apiClient";

// ✅ ManualMimshakDataService יורש מ-BaseService אבל משתמש ב-composition עם MimshakDataServiceBase
export class ManualMimshakDataService extends BaseService<ManualMimshakData> {
  private mimshakService: MimshakDataServiceBase;

  constructor() {
    super("/manual_mimshak_data");
    // יצירת instance של MimshakDataServiceBase עבור manual_mimshak_data
    this.mimshakService = new (class extends MimshakDataServiceBase {
      constructor(apiUrl: string) {
        super(apiUrl);
      }
    })("/manual_mimshak_data");
  }

  async createMimshakData(mimshakData: ManualMimshakData): Promise<ManualMimshakData> {
    return this.create(mimshakData);
  }

  async getLatestRecordsByUser(user_id: number): Promise<ManualMimshakData[]> {
    return this.mimshakService.getLatestRecordsByUser(user_id) as Promise<ManualMimshakData[]>;
  }

  async getByIdentifiers(contact_id: number, product_type: number, policy_number: string, reported_period?: string): Promise<ManualMimshakData | null> {
    return this.mimshakService.getByIdentifiers(contact_id, product_type, policy_number, reported_period) as Promise<ManualMimshakData | null>;
  }

  async getMimshaksDataByUserAndPeriod(contact_id: number, period: string): Promise<ManualMimshakData[]> {
    return this.mimshakService.getMimshaksDataByUserAndPeriod(contact_id, period) as Promise<ManualMimshakData[]>;
  }

  async getMimshaksDataByMultipleUsersAndPeriod(contactIds: number[], period: string): Promise<ManualMimshakData[]> {
    return this.mimshakService.getMimshaksDataByMultipleUsersAndPeriod(contactIds, period) as Promise<ManualMimshakData[]>;
  }

  async getLatestByMultipleContacts(contactIds: number[]): Promise<ManualMimshakData[]> {
    return this.mimshakService.getLatestByMultipleContacts(contactIds) as Promise<ManualMimshakData[]>;
  }

  async getByContactSorted(params: {
    contact_ids: Array<number>;
    handler_code?: string;
    product_type?: number;
    policy_number?: string;
    skip?: number;
    limit?: number;
  }): Promise<ManualMimshakData[]> {
    return this.mimshakService.getByContactSorted(params) as Promise<ManualMimshakData[]>;
  }

  async groupByContactId() {
    return this.mimshakService.groupByContactId();
  }

  async groupByHandlerReportedPeriod(reportOrigin?: number, minReportedPeriod?: string) {
    return this.mimshakService.groupByHandlerReportedPeriod(reportOrigin, minReportedPeriod);
  }

  async groupByReportedHandlerProduct(reportOrigin?: number, minReportedPeriod?: string) {
    return this.mimshakService.groupByReportedHandlerProduct(reportOrigin, minReportedPeriod);
  }

  async groupByReportedHandlerProductNewPolicy() {
    return this.mimshakService.groupByReportedHandlerProductNewPolicy();
  }

  async groupByReportedHandlerProductNewForAgentPolicy() {
    return this.mimshakService.groupByReportedHandlerProductNewForAgentPolicy();
  }

  async compareUserPolicies(userId: number, currentReportDate: string, previousReportDate?: string) {
    return this.mimshakService.compareUserPolicies(userId, currentReportDate, previousReportDate);
  }

  async compareAllPolicies(currentPeriod: string, previousPeriod: string) {
    return this.mimshakService.compareAllPolicies(currentPeriod, previousPeriod);
  }

  async comparePoliciesForContact(contactId: number, currentPeriod: string, previousPeriod: string) {
    return this.mimshakService.comparePoliciesForContact(contactId, currentPeriod, previousPeriod);
  }

  async groupByUserProductHandler() {
    return this.mimshakService.groupByUserProductHandler();
  }

  async groupByReportedHandlerProductForUsers(userIds: number[], reportedPeriod?: string) {
    return this.mimshakService.groupByReportedHandlerProductForUsers(userIds, reportedPeriod);
  }

  async fetchGroupedCodeCounts(reportedPeriod: string) {
    return this.mimshakService.fetchGroupedCodeCounts(reportedPeriod);
  }

  async getMonthlyReturnsForUsers(userIds: number[], productTypes: number[]) {
    return this.mimshakService.getMonthlyReturnsForUsers(userIds, productTypes);
  }

  async getDistinctReportedPeriodsForUser(userId: number) {
    return this.mimshakService.getDistinctReportedPeriodsForUser(userId);
  }

  async getDistinctReportedPeriodsForContacts(contactIds: number[]) {
    return this.mimshakService.getDistinctReportedPeriodsForContacts(contactIds);
  }

  async getLightFilteredMimshakData(
    contact_id?: number,
    handler_code?: string,
    reported_period?: string,
    product_type?: string,
    policy_number?: string,
    agent_code?: string,
    skip: number = 0,
    limit: number = 1000,
    report_origin?: number
  ): Promise<ManualMimshakData[]> {
    return this.mimshakService.getLightFilteredMimshakData(
      contact_id,
      handler_code,
      reported_period,
      product_type,
      policy_number,
      agent_code,
      skip,
      limit,
      report_origin
    ) as Promise<ManualMimshakData[]>;
  }

  async getLightTransferMimshakData(
    from_period?: string,
    to_period?: string,
    contact_ids?: number[],
    handler_code?: string,
    agent_code?: string,
    product_type?: string,
    policy_number?: string,
    skip: number = 0,
    limit: number = 400
  ): Promise<ManualMimshakData[]> {
    return this.mimshakService.getLightTransferMimshakData(
      from_period,
      to_period,
      contact_ids,
      handler_code,
      agent_code,
      product_type,
      policy_number,
      skip,
      limit
    ) as Promise<ManualMimshakData[]>;
  }

  async deleteByIdentifiers(
    contact_id: number,
    handler_code: string,
    product_type: number,
    policy_number: string
  ): Promise<{ message: string; deleted_count: number }> {
    const params = new URLSearchParams({
      contact_id: contact_id.toString(),
      handler_code,
      product_type: product_type.toString(),
      policy_number
    });

    const response = await apiClient.delete(`${this.baseUrl}/by-identifiers?${params}`);
    return response.data;
  }

  async updateIdentifiers(
    contact_id: number,
    old_handler_code: string,
    old_product_type: number,
    old_policy_number: string,
    new_handler_code: string,
    new_product_type: number,
    new_policy_number: string
  ): Promise<{ message: string; updated_count: number }> {
    const response = await apiClient.patch(
      `${this.baseUrl}/update-identifiers/${contact_id}`,
      {
        old_handler_code,
        old_product_type,
        old_policy_number,
        new_handler_code,
        new_product_type,
        new_policy_number
      }
    );
    return response.data;
  }

}

export const manualMimshakDataService = new ManualMimshakDataService();
